<template>
    <ValidationObserver v-slot="{ invalid }">
        <form action="" method="POST" @submit.prevent="save">
            <v-row>
                <v-col cols="12" md="8">
                    <v-card :class="{ jsCardHasError: invalid }">
                        <v-container class="EditorForm">
                            <!--
                            Field Name
                            -->
                            <v-row no-gutters>
                                <v-col cols="12">
                                    <ValidationProvider name="name" rules="required|max:255" v-slot="{ errors }">
                                        <v-text-field
                                                label="Field Name"
                                                name="name"
                                                id="name"
                                                v-model="name"
                                                outlined
                                                :error-messages="errors[0]"
                                        ></v-text-field>
                                    </ValidationProvider>
                                </v-col>
                            </v-row>

                            <!--
                            Card & Field Type Selectors
                            -->
                            <v-row dense>
                                <!--
                                Card Selector
                                -->
                                <v-col cols="12" sm="6">
                                    <ValidationProvider name="section" rules="required" v-slot="{ errors }">
                                        <v-autocomplete
                                                :items="cardsForSelect()"
                                                item-text="name"
                                                item-value="id"
                                                name="card_id"
                                                id="card_id"
                                                label="Parent Card"
                                                v-model="card"
                                                outlined
                                                :error-messages="errors[0]"
                                        >
                                            <template v-slot:item="data">
                                                <template>
                                                    <v-list-item-content>
                                                        <v-list-item-title v-html="data.item.name"></v-list-item-title>
                                                        <v-list-item-subtitle
                                                                v-html="data.item.group"
                                                        ></v-list-item-subtitle>
                                                    </v-list-item-content>
                                                </template>
                                            </template>
                                        </v-autocomplete>
                                    </ValidationProvider>
                                </v-col>

                                <!--
                                Field Type Selector
                                -->
                                <v-col cols="12" sm="6">
                                    <ValidationProvider name="section" rules="required" v-slot="{ errors }">
                                        <v-autocomplete
                                                :items="types"
                                                item-text="name"
                                                item-value="type"
                                                name="type_id"
                                                id="type_id"
                                                label="Field Type"
                                                v-model="type"
                                                outlined
                                                :error-messages="errors[0]"
                                        ></v-autocomplete>
                                    </ValidationProvider>
                                </v-col>
                            </v-row>

                            <!--
                            Group Options
                            -->
                            <!------------------------------------------------------------------------------------------------->
                            <div class="EditorForm__group" v-if="type === 'group'">
                                <h2 class="EditorForm__group__heading">Repeating Group Settings</h2>
                                <HelpModal button-color="secondary"
                                           text="<p><i class=&quot;fas fa-exclamation-triangle&quot;>&nbsp;&nbsp;</i><strong>Removing fields from groups or iterations of groups WILL destroy user data.</strong> Each <em>Population</em> (Profession-based, default Field Entry) and <em>Submission</em> (User Field Entry) in the database will be affected.</p>
                                           <p><strong>Removing a group iteration</strong> from a field will remove the last iteration's data from all Populations and Submissions.</p>
                                           <p><strong>Adding a field</strong> is non-destructive, and will simply add a field to the end of a group. <em>Populations</em> and <em>Submissions</em> will have a blank field appended to the end of the group. This blank field can be populated normally.</p>
                                           <p><strong>Removing a field</strong> removes the field from ALL Populations and Submissions. This cannot be rolled back.</p>
                                           <p><strong>Inline</strong> Sets fields next to each other as opposed to stacking them. Inline groups work best with an even number of fields.</p>
                                           <p><strong>Numbered</strong> Selecting numbered will add an incremental group number to the end of the field's label (Benefit 1, Benefit 2, etc...). Applies only if the Repeat Group setting is > 1.</p>"
                                           title="Repeating Group Fields"
                                ></HelpModal>
                                <div class="EditorForm__row">
                                    <div class="EditorForm__field EditorForm__field--third EditorForm__field--adjust">
                                        <ValidationProvider name="numberOfFieldsInGroup" rules="required" v-slot="{ errors }">
                                            <v-text-field
                                                    label="Repeat"
                                                    name="numFieldsGroup"
                                                    id="numFieldsGroup"
                                                    v-model="fieldsInGroup"
                                                    outlined
                                                    dense
                                                    :error-messages="errors[0]"
                                                    type="number"
                                                    suffix="times"
                                            ></v-text-field>
                                        </ValidationProvider>
                                        <v-tooltip top color="black" open-delay="1000">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon
                                                       small
                                                       v-bind="attrs"
                                                       v-on="on"
                                                       color="secondary"
                                                       @click="initAdjustGroupIteration"
                                                >
                                                    <v-icon small>fas fa-check-circle</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Set Repetition</span>
                                        </v-tooltip>

                                        <v-tooltip top color="black" open-delay="1000">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon
                                                       small
                                                       v-bind="attrs"
                                                       v-on="on"
                                                       @click="resetGroupIteration"
                                                >
                                                    <v-icon small>fas fa-times-circle</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Reset Repetition</span>
                                        </v-tooltip>
                                    </div>
                                    <div class="EditorForm__field EditorForm__field--third EditorForm__field--checkboxes">
                                        <v-checkbox class="EditorForm__field__checkbox" dense color="secondary" label="Inline" v-model="groupInline"></v-checkbox>
                                        <v-checkbox class="EditorForm__field__checkbox" dense color="secondary" label="Numbered" v-model="groupNumbered"></v-checkbox>
                                    </div>
                                </div>

                                <h2 class="EditorForm__group__heading">Repeating Group Fields</h2>
                                <div class="EditorForm__row" v-for="(field, index) in groupFields" :key="index+field.type">
                                    <div class="EditorForm__group__subheading">
                                        <v-tooltip right color="black" open-delay="1000">
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-btn icon
                                                       x-small
                                                       v-bind="attrs"
                                                       v-on="on"
                                                       color="#bcbcbc"
                                                       @click="initRemoveGroupField(index)"
                                                >
                                                    <v-icon x-small>fas fa-minus-circle</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>Remove Field {{index+1}}</span>
                                        </v-tooltip>
                                        <h3>Field {{index+1}}</h3>
                                    </div>
                                    <div class="EditorForm__field EditorForm__field--half" v-if="groupReady">
                                        <ValidationProvider name="groupFieldType" rules="required" v-slot="{ errors }">
                                            <v-autocomplete
                                                    :items="allGroupFieldTypes"
                                                    item-text="name"
                                                    item-value="type"
                                                    name="groupFieldType"
                                                    id="groupFieldType"
                                                    label="Field Type"
                                                    v-model="groupFieldTypeSelections[index]"
                                                    outlined
                                                    dense
                                                    :error-messages="errors[0]"
                                                    @change="saveGroup"
                                            ></v-autocomplete>
                                        </ValidationProvider>
                                    </div>
                                    <div class="EditorForm__field EditorForm__field--half" v-if="groupReady">
                                        <ValidationProvider name="groupFieldName" rules="required" v-slot="{ errors }">
                                            <v-text-field
                                                    label="Field Name"
                                                    name="groupFieldName"
                                                    id="groupFieldName"
                                                    v-model="groupFieldNameSelections[index]"
                                                    outlined
                                                    dense
                                                    :error-messages="errors[0]"
                                                    @change="saveGroup"
                                            ></v-text-field>
                                        </ValidationProvider>
                                    </div>
                                </div>

                                <div class="EditorForm__row EditorForm__row--actions">
                                    <v-btn @click="addField" small color="secondary">
                                        <v-icon left small>fas fa-plus-circle</v-icon>
                                        Group Field
                                    </v-btn>
                                </div>
                            </div>
                            <!------------------------------------------------------------------------------------------------->

                            <!--
                            Audio
                            -->
                            <v-row dense v-if="type === 'audio-player'">
                                <v-col cols="12">
                                    <ValidationProvider name="audio" rules="url" v-slot="{ errors }">
                                        <v-text-field
                                                label="Audio URL"
                                                name="audio"
                                                id="audio"
                                                v-model="audio"
                                                outlined
                                                :error-messages="errors[0]"
                                                type="url"
                                        ></v-text-field>
                                    </ValidationProvider>
                                </v-col>
                            </v-row>

                            <!--
                            Hero Selector
                            -->
                            <div v-if="type === 'hero-selector'" class="OptionsGroup">
                                <h2 class="OptionsGroup__title">Hero Selector Settings</h2>
                                <v-row dense>
                                    <v-col cols="12" sm="4">
                                        <ValidationProvider name="heroSelectorLabel" rules="max:255" v-slot="{ errors }">
                                            <v-text-field
                                                    label="Selector Label"
                                                    name="heroSelectorLabel"
                                                    id="heroSelectorLabel"
                                                    v-model="heroSelectorLabel"
                                                    outlined
                                                    :error-messages="errors[0]"
                                            ></v-text-field>
                                        </ValidationProvider>
                                    </v-col>

                                    <v-col cols="12" sm="4">
                                        <ValidationProvider name="heroSelectorChoiceOne" rules="max:255" v-slot="{ errors }">
                                            <v-text-field
                                                    label="Choice 1 Label"
                                                    name="heroSelectorChoiceOne"
                                                    id="heroSelectorChoiceOne"
                                                    v-model="heroSelectorChoiceOne"
                                                    outlined
                                                    :error-messages="errors[0]"
                                            ></v-text-field>
                                        </ValidationProvider>
                                    </v-col>

                                    <v-col cols="12" sm="4">
                                        <ValidationProvider name="heroSelectorChoiceTwo" rules="max:255" v-slot="{ errors }">
                                            <v-text-field
                                                    label="Choice 2 Label"
                                                    name="heroSelectorChoiceTwo"
                                                    id="heroSelectorChoiceTwo"
                                                    v-model="heroSelectorChoiceTwo"
                                                    outlined
                                                    :error-messages="errors[0]"
                                            ></v-text-field>
                                        </ValidationProvider>
                                    </v-col>
                                </v-row>
                            </div>

                            <!--
                            File Upload
                            -->
                            <div v-if="type === 'image-upload' || type === 'hero-selector' " class="OptionsGroup">
                                <h2 class="OptionsGroup__title">Media Upload Settings</h2>
                                <v-row dense>
                                    <v-col cols="12" sm="6">
                                        <h3 class="OptionsGroup__subtitle">Show Stock Media</h3>
                                        <v-radio-group v-model="loadStock" row>
                                            <v-radio label="Yes" value="yes"></v-radio>
                                            <v-radio label="No" value="no"></v-radio>
                                        </v-radio-group>
                                    </v-col>

                                    <v-col cols="12" sm="6">
                                        <h3 class="OptionsGroup__subtitle">Media Upload Type</h3>
                                        <v-radio-group v-model="uploadType" row>
                                            <v-radio label="Image" value="image"></v-radio>
                                            <v-radio label="PDF" value="pdf"></v-radio>
                                        </v-radio-group>
                                    </v-col>

                                    <v-col cols="12" sm="6">
                                        <h3 class="OptionsGroup__subtitle">Show Select Button</h3>
                                        <v-radio-group v-model="showSelectButton" row>
                                            <v-radio label="Yes" value="yes"></v-radio>
                                            <v-radio label="No" value="no"></v-radio>
                                        </v-radio-group>
                                    </v-col>

                                    <v-col cols="12" sm="6">
                                        <h3 class="OptionsGroup__subtitle">Show Upload Button</h3>
                                        <v-radio-group v-model="showUploadButton" row>
                                            <v-radio label="Yes" value="yes"></v-radio>
                                            <v-radio label="No" value="no"></v-radio>
                                        </v-radio-group>
                                    </v-col>

                                    <v-col cols="12" sm="12">
                                        <ValidationProvider name="uploaderInstructionText" rules="max:255" v-slot="{ errors }">
                                            <v-text-field
                                                    label="Uploader Instruction Text"
                                                    name="uploaderInstructionText"
                                                    id="uploaderInstructionText"
                                                    v-model="uploaderInstructionText"
                                                    outlined
                                                    :error-messages="errors[0]"
                                            ></v-text-field>
                                        </ValidationProvider>
                                    </v-col>
                                </v-row>
                            </div>

                            <!--
                            Color Upsell
                            -->
                            <div v-if="type === 'color-select'" class="OptionsGroup">
                                <h2 class="OptionsGroup__title">Custom Color Settings</h2>
                                <v-row dense>
                                    <v-col cols="12" sm="4">
                                        <h3 class="OptionsGroup__subtitle">Access Level</h3>
                                        <v-radio-group dense
                                                       v-model="customColorAccessLevel"
                                        >
                                            <v-radio
                                                    label="Basic"
                                                    value="basic"
                                                    dense
                                            ></v-radio>
                                            <v-radio
                                                    label="Premium"
                                                    value="premium"
                                                    dense
                                            ></v-radio>
                                            <v-radio
                                                    label="Plus"
                                                    value="plus"
                                                    dense
                                            ></v-radio>
                                        </v-radio-group>
                                    </v-col>

                                    <v-col cols="12" sm="8">
                                        <v-textarea label="Upsell"
                                                    v-model="customColorUpsell"
                                                    outlined
                                        ></v-textarea>
                                    </v-col>
                                </v-row>
                            </div>

                            <!--
                            Font Upsell
                            -->
                            <div v-if="type === 'font-select'" class="OptionsGroup">
                                <h2 class="OptionsGroup__title">Custom Font Settings</h2>
                                <v-row dense v-if="type === 'font-select'">
                                    <v-col cols="12" sm="4">
                                        <h3 class="OptionsGroup__subtitle">Access Level</h3>
                                        <v-radio-group dense
                                                       v-model="customFontAccessLevel"
                                        >
                                            <v-radio
                                                    label="Basic"
                                                    value="basic"
                                                    dense
                                            ></v-radio>
                                            <v-radio
                                                    label="Premium"
                                                    value="premium"
                                                    dense
                                            ></v-radio>
                                            <v-radio
                                                    label="Plus"
                                                    value="plus"
                                                    dense
                                            ></v-radio>
                                        </v-radio-group>
                                    </v-col>

                                    <v-col cols="12" sm="8">
                                        <v-textarea label="Upsell"
                                                    v-model="customFontUpsell"
                                                    outlined
                                        ></v-textarea>
                                    </v-col>
                                </v-row>
                            </div>

                            <!--
                            Website Preview
                            -->
                            <div v-if="type === 'site-preview'" class="OptionsGroup">
                                <h2 class="OptionsGroup__title">Site Preview Settings</h2>
                                <h3 class="OptionsGroup__subtitle">Hero Image</h3>
                                <ImageSelector v-if="type === 'site-preview'"
                                               field-namespace="heroImage"
                                               key="heroImage"
                                               :selected-image-id="previewHeroImage ? previewHeroImage : ''"
                                               load-stock-library="yes"
                                               :load-user-library="false"
                                               upload-type="image"
                                               :hide-label="true"
                                />

                                <h3 class="OptionsGroup__subtitle">Headshot</h3>
                                <ImageSelector v-if="type === 'site-preview'"
                                               field-namespace="headshot"
                                               key="headshot"
                                               :selected-image-id="previewHeadshot ? previewHeadshot : ''"
                                               load-stock-library="yes"
                                               :load-user-library="false"
                                               upload-type="image"
                                               :hide-label="true"
                                />

                                <h3 class="OptionsGroup__subtitle">Content</h3>
                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field label="Preview Hero Headline"
                                                      v-model="previewHeroHeadline"
                                                      outlined
                                        ></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field label="Preview Hero Subhead"
                                                      v-model="previewHeroSubhead"
                                                      outlined
                                        ></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field label="Preview Hero Button Text"
                                                      v-model="previewHeroButtonText"
                                                      outlined
                                        ></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row dense>
                                    <v-col cols="12" sm="4">
                                        <v-text-field label="Banner Item 1"
                                                      v-model="previewBanner1"
                                                      outlined
                                        ></v-text-field>
                                    </v-col>

                                    <v-col cols="12" sm="4">
                                        <v-text-field label="Banner Item 2"
                                                      v-model="previewBanner2"
                                                      outlined
                                        ></v-text-field>
                                    </v-col>

                                    <v-col cols="12" sm="4">
                                        <v-text-field label="Banner Item 3"
                                                      v-model="previewBanner3"
                                                      outlined
                                        ></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field label="Preview Body Heading"
                                                      v-model="previewBodyHeadline"
                                                      outlined
                                        ></v-text-field>
                                    </v-col>
                                </v-row>

                                <v-row dense>
                                    <v-col cols="12">
                                        <v-textarea label="Preview Body Copy"
                                                    v-model="previewBodyCopy"
                                                    outlined
                                        ></v-textarea>
                                    </v-col>
                                </v-row>

                                <v-row dense>
                                    <v-col cols="12">
                                        <v-text-field label="Preview Body Button Text"
                                                      v-model="previewBodyButtonText"
                                                      outlined
                                        ></v-text-field>
                                    </v-col>
                                </v-row>
                            </div>

                            <!--
                            Static Text
                            -->
                            <v-row dense>
                                <v-col v-if="type === 'static-text'" cols="12">
                                    <label>Static Text</label>
                                    <quill-editor v-model="staticText"></quill-editor>
                                </v-col>
                            </v-row>

                            <!--
                            Training
                            -->
                            <div v-if="type === 'training'" class="OptionsGroup">
                                <h2 class="OptionsGroup__title">Training/Tutorial Settings</h2>
                                <v-row dense>
                                    <v-col cols="12" >
                                        <ValidationProvider name="upsell" rules="max:255" v-slot="{ errors }">
                                            <v-text-field
                                                    label="Training Video ID"
                                                    name="trainingVideoId"
                                                    id="trainingVideoId"
                                                    v-model="trainingVideoId"
                                                    outlined
                                                    :error-messages="errors[0]"
                                            ></v-text-field>
                                        </ValidationProvider>
                                        <ValidationProvider name="upsell" rules="max:10000" v-slot="{ errors }">
                                            <v-textarea
                                                    label="Training Description"
                                                    name="trainingDescription"
                                                    id="trainingDescription"
                                                    v-model="trainingDescription"
                                                    outlined
                                                    :error-messages="errors[0]"
                                            ></v-textarea>
                                        </ValidationProvider>

                                        <h3 class="OptionsGroup__subtitle">Thumbnail</h3>
                                        <ImageSelector v-if="type === 'training'"
                                                       field-namespace="trainingVideoThumbnail"
                                                       key="trainingVideoThumbnail"
                                                       :selected-image-id="trainingVideoThumbnail.id ? trainingVideoThumbnail.id : ''"
                                                       load-stock-library="yes"
                                                       :load-user-library="false"
                                                       upload-type="image"
                                                       :hide-label="true"
                                        />

                                        <h3 class="OptionsGroup__subtitle">Download</h3>
                                        <ImageSelector v-if="type === 'training'"
                                                       field-namespace="trainingDownloadFile"
                                                       key="trainingDownloadFile"
                                                       :selected-image-id="trainingDownloadID ? trainingDownloadID : ''"
                                                       load-stock-library="yes"
                                                       :load-user-library="false"
                                                       upload-type="pdf"
                                                       :hide-label="true"
                                        />
                                    </v-col>
                                </v-row>
                            </div>

                            <!--
                            Help Text Options
                            -->
                            <div class="OptionsGroup"
                                 v-if="type !== 'static-text'
                                       && type !== 'audio-player'
                                       && type !== 'site-preview'
                            ">
                                <!--
                                Help Title
                                -->
                                <h2 class="OptionsGroup__title">Help Modal Settings</h2>
                                <v-row dense>
                                    <v-col cols="12">
                                        <ValidationProvider name="upsell" rules="max:255" v-slot="{ errors }">
                                            <v-text-field
                                                    label="Help Modal Title"
                                                    name="helpTitle"
                                                    id="helpTitle"
                                                    v-model="helpTitle"
                                                    outlined
                                                    :error-messages="errors[0]"
                                            ></v-text-field>
                                        </ValidationProvider>
                                    </v-col>
                                </v-row>

                                <!--
                                Help Text
                                -->
                                <v-row dense>
                                    <v-col cols="12">
                                        <label class="OptionsGroup__subtitle">Help Modal Text</label>
                                        <quill-editor v-model="helpText"></quill-editor>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-container>
                    </v-card>
                </v-col>

                <!--
                ------------------------------------------------------------------------------------------------------------
                Controls/Settings
                ------------------------------------------------------------------------------------------------------------
                -->
                <v-col cols="12" md="4">
                    <v-card class="FieldSettings">
                        <v-card-text>
                            <v-row dense v-if="type !== 'site-preview'
                                         && type !== 'font-select'
                                         && type !== 'color-select'"
                            >
                                <!--
                                Location
                                -->
                                <v-col cols="12">
                                    Location: {{setLocation()}}
                                </v-col>

                                <!--
                                Published
                                -->
                                <v-col cols="12">
                                    <v-switch v-model="published"
                                              :label="publishedText"
                                              dense
                                    />
                                </v-col>

                                <!--
                                Access Level
                                -->
                                <v-select label="Access Level" :items="levels" outlined dense v-model="level"></v-select>

                                <!--
                                Order
                                -->
                                <v-col cols="12"
                                       v-if="type !== 'site-preview'
                                               && type !== 'font-select'
                                               && type !== 'color-select'"
                                >
                                    <ValidationProvider name="order" rules="integer" v-slot="{ errors }">
                                        <v-text-field
                                                label="Order"
                                                name="order"
                                                id="order"
                                                v-model="order"
                                                outlined
                                                :error-messages="errors[0]"
                                                type="number"
                                                dense
                                        ></v-text-field>
                                    </ValidationProvider>
                                </v-col>
                            </v-row>

                            <!--
                            Upsell
                            -->
                            <v-row dense v-if="level !== 'basic' && level === 'forfutureuse'">
                                <v-col cols="12">
                                    <ValidationProvider name="upsell" rules="max:255" v-slot="{ errors }">
                                        <v-textarea
                                                label="Upsell"
                                                name="upsell"
                                                id="upsell"
                                                v-model="upsell"
                                                outlined
                                                :error-messages="errors[0]"
                                                dense
                                        ></v-textarea>
                                    </ValidationProvider>
                                </v-col>
                            </v-row>
                        </v-card-text>
                        <v-card-actions>
                            <v-btn text @click="toggleDeleteModal(true)"
                            >Delete Field
                            </v-btn
                            >
                            <v-spacer></v-spacer>
                            <v-btn type="submit" color="primary">
                                <v-icon left small>fas fa-save</v-icon>
                                Save
                            </v-btn>
                        </v-card-actions>
                    </v-card>

                    <!--
                    Field Structure (Populations)
                    -->
                   <FieldStructure v-if="type === 'text'
                                         || type === 'textarea'
                                         || type === 'wysiwyg'
                                         || type === 'group'"
                   />
                </v-col>
            </v-row>
        </form>
        <v-dialog max-width="500" v-model="showRemoveGroupFieldModal">
            <v-card>
                <v-card-title>
                    <v-icon left>fas fa-exclamation-triangle</v-icon> Remove Field
                </v-card-title>
                <v-card-text>
                    <p>Remove <strong>{{ groupFieldTypeSelections[workingGroupIndex] }}</strong> field <strong>{{ groupFieldNameSelections[workingGroupIndex] }}</strong>?</p>
                    <p>Removing this field will remove all of it's Populations and Submissions. Clicking "Remove" will start the removal process immediately. This cannot be undone. Continue? </p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelRemoveGroupField">Cancel</v-btn>
                    <v-btn color="primary" @click="removeGroupField(workingGroupIndex)">Remove</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-dialog max-width="500" v-model="showAdjustGroupIterationModal">
            <v-card>
                <v-card-title>
                    <v-icon left>fas fa-exclamation-triangle</v-icon> Adjust Group Iteration
                </v-card-title>
                <v-card-text>
                    <p><strong>Adding a group iteration</strong> simply adds an iteration to the end of the group. This iteration can be populated/submitted normally.</p>
                    <p><strong>Removing a group iteration</strong> will remove the last iteration from each population/submission.</p>
                </v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn text @click="cancelAdjustGroupIteration">Reset</v-btn>
                    <v-btn color="primary" @click="performAdjustGroupIteration">Adjust</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </ValidationObserver>
</template>

<script>
import { mapMutations, mapActions, mapGetters, mapState } from 'vuex';
import store from '../../../../store/store';
import FieldStructure from '@/views/Admin/Config/Field/FieldStructure';
import ImageSelector from '@/components/ImageSelector/ImageSelector';
import { EventBus } from '@/store/eventBus';
import getImageById from '@/components/ImageSelector/getImageById';
import HelpModal from '@/components/HelpModal';
import axios from 'axios';

export default {
    components: { HelpModal, ImageSelector, FieldStructure },
    computed: {
        fieldId: function() {
            return this.$route.params.id;
        },

        card: {
            get: function() {
                return store.state.fieldEditor.card;
            },
            set: function(value) {
                this.setCard(value);
            }
        },

        type: {
            get: function() {
                return store.state.fieldEditor.type;
            },
            set: function(value) {
                this.setType(value);
            }
        },

        name: {
            get: function() {
                return store.state.fieldEditor.name;
            },
            set: function(value) {
                this.setName(value);
            }
        },

        order: {
            get: function() {
                return store.state.fieldEditor.order;
            },
            set: function(value) {
                this.setOrder(value);
            }
        },

        published: {
            get: function() {
                return store.state.fieldEditor.published;
            },
            set: function(value) {
                this.setPublished(value);
            }
        },

        level: {
            get: function() {
                return store.state.fieldEditor.level;
            },
            set: function(value) {
                this.setLevel(value);
            }
        },

        /**
         * Options
         */

        staticText: {
            get: function() {
                return store.state.fieldEditor.options.staticText;
            },
            set: function(value) {
                this.setStaticText(value);
            }
        },

        upsell: {
            get: function() {
                return store.state.fieldEditor.options.upsell;
            },
            set: function(value) {
                this.setUpsell(value);
            }
        },

        customColorAccessLevel: {
            get: function() {
                return store.state.fieldEditor.options.customColorAccessLevel;
            },
            set: function(value) {
                this.setCustomColorAccessLevel(value);
            }
        },

        customColorUpsell: {
            get: function() {
                return store.state.fieldEditor.options.customColorUpsell;
            },
            set: function(value) {
                this.setCustomColorUpsell(value);
            }
        },

        customFontAccessLevel: {
            get: function() {
                return store.state.fieldEditor.options.customFontAccessLevel;
            },
            set: function(value) {
                this.setCustomFontAccessLevel(value);
            }
        },

        customFontUpsell: {
            get: function() {
                return store.state.fieldEditor.options.customFontUpsell;
            },
            set: function(value) {
                this.setCustomFontUpsell(value);
            }
        },

        helpText: {
            get: function() {
                return store.state.fieldEditor.options.helpText;
            },
            set: function(value) {
                this.setHelpText(value);
            }
        },

        helpTitle: {
            get: function() {
                return store.state.fieldEditor.options.helpTitle;
            },
            set: function(value) {
                this.setHelpTitle(value);
            }
        },

        audio: {
            get: function() {
                return store.state.fieldEditor.options.audio;
            },
            set: function(value) {
                this.setAudio(value);
            }
        },

        uploadType: {
            get: function() {
                return store.state.fieldEditor.options.uploadType;
            },
            set: function(value) {
                this.setUploadType(value);
            }
        },

        showSelectButton: {
            get: function() {
                return store.state.fieldEditor.options.showSelectButton;
            },
            set: function(value) {
                this.setShowSelectButton(value);
            }
        },

        showUploadButton: {
            get: function() {
                return store.state.fieldEditor.options.showUploadButton;
            },
            set: function(value) {
                this.setShowUploadButton(value);
            }
        },

        uploaderInstructionText: {
            get: function() {
                return store.state.fieldEditor.options.uploaderInstructionText;
            },
            set: function(value) {
                this.setUploaderInstructionText(value);
            }
        },

        loadStock: {
            get: function() {
                return store.state.fieldEditor.options.loadStock;
            },
            set: function(value) {
                this.setLoadStock(value);
            }
        },

        groupFieldType: {
            get: function() {
                return store.state.fieldEditor.options.group.type;
            },
            set: function(value) {
                this.setGroupFieldType(value);
            }
        },

        groupFieldName: {
            get: function() {
                return store.state.fieldEditor.options.group.name;
            },
            set: function(value) {
                this.setFieldsInGroup(value);
            }
        },

        fieldsInGroup: {
            get: function() {
                return store.state.fieldEditor.options.fieldsInGroup;
            },
            set: function(value) {
                this.setFieldsInGroup(value);
            }
        },

        groupInline: {
            get: function() {
                return store.state.fieldEditor.options.groupInline;
            },
            set: function(value) {
                this.setGroupInline(value);
            }
        },

        groupNumbered: {
            get: function() {
                return store.state.fieldEditor.options.groupNumbered;
            },
            set: function(value) {
                this.setGroupNumbered(value);
            }
        },

        optionsGroupFields: {
            get: function() {
                return store.state.fieldEditor.options.group.fields;
            },
            set: function(value) {
                this.setOptionsGroup(value);
            }
        },

        textFieldName: {
            get: function() {
                return store.state.fieldEditor.options.textFieldName;
            },
            set: function(value) {
                this.setTextFieldName(value);
            }
        },

        textField2Name: {
            get: function() {
                return store.state.fieldEditor.options.textField2Name;
            },
            set: function(value) {
                this.setTextField2Name(value);
            }
        },

        textareaName: {
            get: function() {
                return store.state.fieldEditor.options.textareaName;
            },
            set: function(value) {
                this.setTextareaName(value);
            }
        },

        previewHeroHeadline: {
            get: function() {
                return store.state.fieldEditor.options.preview.heroHeadline;
            },
            set: function(value) {
                this.setPreviewHeroHeadline(value);
            }
        },

        previewHeroSubhead: {
            get: function() {
                return store.state.fieldEditor.options.preview.heroSubhead;
            },
            set: function(value) {
                this.setPreviewHeroSubhead(value);
            }
        },

        previewHeroButtonText: {
            get: function() {
                return store.state.fieldEditor.options.preview.heroButtonText;
            },
            set: function(value) {
                this.setPreviewHeroButtonText(value);
            }
        },

        previewBodyHeadline: {
            get: function() {
                return store.state.fieldEditor.options.preview.bodyHeadline;
            },
            set: function(value) {
                this.setPreviewBodyHeadline(value);
            }
        },

        previewBodyCopy: {
            get: function() {
                return store.state.fieldEditor.options.preview.bodyCopy;
            },
            set: function(value) {
                this.setPreviewBodyCopy(value);
            }
        },

        previewBodyButtonText: {
            get: function() {
                return store.state.fieldEditor.options.preview.bodyButtonText;
            },
            set: function(value) {
                this.setPreviewBodyButtonText(value);
            }
        },

        previewBanner1: {
            get: function() {
                return store.state.fieldEditor.options.preview.banner1;
            },
            set: function(value) {
                this.setPreviewBanner1(value);
            }
        },

        previewBanner2: {
            get: function() {
                return store.state.fieldEditor.options.preview.banner2;
            },
            set: function(value) {
                this.setPreviewBanner2(value);
            }
        },

        previewBanner3: {
            get: function() {
                return store.state.fieldEditor.options.preview.banner3;
            },
            set: function(value) {
                this.setPreviewBanner3(value);
            }
        },

        previewHeroImage: {
            get: function() {
                return store.state.fieldEditor.options.preview.heroImage;
            },
            set: function(value) {
                this.setPreviewHeroImage(value);
            }
        },

        previewHeadshot: {
            get: function() {
                return store.state.fieldEditor.options.preview.headshot;
            },
            set: function(value) {
                this.setPreviewHeadshot(value);
            }
        },

        trainingVideoThumbnail: {
            get: function() {
                return store.state.fieldEditor.options.training.video.thumbnail;
            },
            set: function(value) {
                this.setTrainingVideoThumbnail(value);
            }
        },

        trainingVideoId: {
            get: function() {
                return store.state.fieldEditor.options.training.video.id;
            },
            set: function(value) {
                this.setTrainingVideoId(value);
            }
        },

        trainingDownloadID: {
            get: function() {
                return store.state.fieldEditor.options.training.download.id;
            },
            set: function(value) {
                this.setTrainingDownloadId(value);
                getImageById(value)
                    .then((response) => {
                        this.setTrainingDownloadURL(response.directory + '/' + response.filename + '.' + response.extension);
                    });
            }
        },

        trainingDescription: {
            get: function() {
                return store.state.fieldEditor.options.training.description;
            },
            set: function(value) {
                this.setTrainingDescription(value);
            }
        },

        heroSelectorLabel: {
            get: function() {
                return store.state.fieldEditor.options.heroSelector.label;
            },
            set: function(value) {
                this.setHeroSelectorLabel(value);
            }
        },

        heroSelectorChoiceOne: {
            get: function() {
                return store.state.fieldEditor.options.heroSelector.choiceOne;
            },
            set: function(value) {
                this.setHeroSelectorChoiceOne(value);
            }
        },

        heroSelectorChoiceTwo: {
            get: function() {
                return store.state.fieldEditor.options.heroSelector.choiceTwo;
            },
            set: function(value) {
                this.setHeroSelectorChoiceTwo(value);
            }
        },

        templateSelectorLabel: {
            get: function() {
                return store.state.fieldEditor.options.templateSelector.label;
            },
            set: function(value) {
                this.setTemplateSelectorLabel(value);
            }
        },

        templateSelectorColorOne: {
            get: function() {
                return store.state.fieldEditor.options.templateSelector.colorOne;
            },
            set: function(value) {
                this.setTemplateSelectorColorOne(value);
            }
        },

        templateSelectorColorTwo: {
            get: function() {
                return store.state.fieldEditor.options.templateSelector.colorTwo;
            },
            set: function(value) {
                this.setTemplateSelectorColorTwo(value);
            }
        },

        templateSelectorColorThree: {
            get: function() {
                return store.state.fieldEditor.options.templateSelector.colorThree;
            },
            set: function(value) {
                this.setTemplateSelectorColorThree(value);
            }
        },

        ...mapState('fieldEditor', [
            'cards',
            'types',
            'populations',
            'deleteModal',
            'cardData',
            'sectionData',
            'stepData',
            'pageData',
        ]),

        ...mapGetters({
            publishedText: 'fieldEditor/publishedText',
        })
    },

    data() {
        return {
            groupReady: false,
            groupFields: [],
            groupFieldTypeSelections: [],
            groupFieldNameSelections: [],
            showRemoveGroupFieldModal: false,
            showAdjustGroupIterationModal: false,
            workingGroupIndex: undefined,
            initialFieldsInGroup: undefined,

            allGroupFieldTypes: [
                {
                    'type': 'text',
                    'name': 'Text',
                },
                {
                    'type': 'textarea',
                    'name': 'Textarea',
                },
            ],

            levels: [
                {
                    'text': 'Support',
                    'value': 'support'
                },
                {
                    'text': 'Basic',
                    'value': 'basic'
                },
                {
                    'text': 'Premium',
                    'value': 'premium'
                },
                {
                    'text': 'Plus',
                    'value': 'plus'
                }
            ]
        };
    },

    mounted() {
        this.getData()
            .then(() => {
                if(this.type === 'group') {
                    this.setGroupData();
                    this.initialFieldsInGroup = this.fieldsInGroup;
                }
            });

        EventBus.$on('imageSelector-trainingVideoThumbnail-selected', data => {
            this.setTrainingVideoThumbnail(data.selectedImageId);
        });

        EventBus.$on('imageSelector-trainingDownloadFile-selected', data => {
            this.setTrainingDownloadId(data.selectedImageId);
            getImageById(data.selectedImageId)
                .then((response) => {
                    this.setTrainingDownloadURL(response.directory + '/' + response.filename + '.' + response.extension);
                });
        });

        EventBus.$on('imageSelector-heroImage-selected', data => {
            this.setPreviewHeroImage(data.selectedImageId);
        });

        EventBus.$on('imageSelector-headshot-selected', data => {
            this.setPreviewHeadshot(data.selectedImageId);
        });
    },

    methods: {
        ...mapActions({
            showNotification: 'notification/show',
            getData: 'fieldEditor/getData',
            saveData: 'fieldEditor/saveData',
            destroyData: 'fieldEditor/destroyData'
        }),
        ...mapMutations({
            toggleOverlay: 'overlay/toggle',
            setId: 'fieldEditor/setFieldId',
            setCard: 'fieldEditor/setCard',
            setType: 'fieldEditor/setType',
            setName: 'fieldEditor/setName',
            setHelpText: 'fieldEditor/setHelpText',
            setHelpTitle: 'fieldEditor/setHelpTitle',
            setInstruction: 'fieldEditor/setInstruction',
            setOrder: 'fieldEditor/setOrder',
            setPublished: 'fieldEditor/setPublished',
            toggleDeleteModal: 'fieldEditor/toggleDeleteModal',
            setLevel: 'fieldEditor/setLevel',
            setAudio: 'fieldEditor/setAudio',
            setStaticText: 'fieldEditor/setStaticText',

            setFieldsInGroup: 'fieldEditor/setFieldsInGroup',
            setGroupInline: 'fieldEditor/setGroupInline',
            setGroupNumbered: 'fieldEditor/setGroupNumbered',
            setOptionsGroup: 'fieldEditor/setOptionsGroup',

            setUpsell: 'fieldEditor/setUpsell',
            setCustomFontAccessLevel: 'fieldEditor/setCustomFontAccessLevel',
            setCustomFontUpsell: 'fieldEditor/setCustomFontUpsell',
            setCustomColorAccessLevel: 'fieldEditor/setCustomColorAccessLevel',
            setCustomColorUpsell: 'fieldEditor/setCustomColorUpsell',
            setPreviewHeroHeadline: 'fieldEditor/setPreviewHeroHeadline',
            setPreviewHeroSubhead: 'fieldEditor/setPreviewHeroSubhead',
            setPreviewHeroButtonText: 'fieldEditor/setPreviewHeroButtonText',
            setPreviewBanner1: 'fieldEditor/setPreviewBanner1',
            setPreviewBanner2: 'fieldEditor/setPreviewBanner2',
            setPreviewBanner3: 'fieldEditor/setPreviewBanner3',
            setPreviewBodyHeadline: 'fieldEditor/setPreviewBodyHeadline',
            setPreviewBodyCopy: 'fieldEditor/setPreviewBodyCopy',
            setPreviewBodyButtonText: 'fieldEditor/setPreviewBodyButtonText',
            setPreviewHeroImage: 'fieldEditor/setPreviewHeroImage',
            setPreviewHeadshot: 'fieldEditor/setPreviewHeadshot',
            setUploadType: 'fieldEditor/setUploadType',
            setLoadStock: 'fieldEditor/setLoadStock',
            setShowSelectButton: 'fieldEditor/setShowSelectButton',
            setShowUploadButton: 'fieldEditor/setShowUploadButton',
            setUploaderInstructionText: 'fieldEditor/setUploaderInstructionText',
            setTrainingVideoThumbnail: 'fieldEditor/setTrainingVideoThumbnail',
            setTrainingVideoId: 'fieldEditor/setTrainingVideoId',
            setTrainingDownloadId: 'fieldEditor/setTrainingDownloadId',
            setTrainingDownloadURL: 'fieldEditor/setTrainingDownloadURL',
            setTrainingDescription: 'fieldEditor/setTrainingDescription',
            setHeroSelectorLabel: 'fieldEditor/setHeroSelectorLabel',
            setHeroSelectorChoiceOne: 'fieldEditor/setHeroSelectorChoiceOne',
            setHeroSelectorChoiceTwo: 'fieldEditor/setHeroSelectorChoiceTwo',
            setTemplateSelectorLabel: 'fieldEditor/setTemplateSelectorLabel',
            setTemplateSelectorColorOne: 'fieldEditor/setTemplateSelectorColorOne',
            setTemplateSelectorColorTwo: 'fieldEditor/setTemplateSelectorColorTwo',
            setTemplateSelectorColorThree: 'fieldEditor/setTemplateSelectorColorThree',
        }),

        setGroupData() {
            this.optionsGroupFields.forEach((field) => {
                this.groupFields = this.optionsGroupFields;
                this.groupFieldTypeSelections.push(field.type);
                this.groupFieldNameSelections.push(field.name);
                this.groupReady = true;
            });
        },

        clearGroupData() {
            return new Promise(resolve => {
                this.groupReady = false;
                this.groupFields = [];
                this.groupFieldTypeSelections = [];
                this.groupFieldNameSelections = [];
                resolve();
            });
        },

        addField() {
            const length = this.groupFieldNameSelections.length;
            this.groupFieldNameSelections.splice(length, 0, '');
            this.groupFieldTypeSelections.splice(length, 0, 'text' );
            this.saveGroup()
                .then(() => {
                    this.clearGroupData()
                        .then(() => {
                            this.setGroupData();
                        });
                });
        },

        initRemoveGroupField(index) {
            this.workingGroupIndex = index;
            this.showRemoveGroupFieldModal = true;
        },

        cancelRemoveGroupField() {
            this.workingGroupIndex = undefined;
            this.showRemoveGroupFieldModal = false;
        },

        removeGroupField(index) {
            this.showRemoveGroupFieldModal = false;
            axios.post('/api/field/group/edit-data/' + this.fieldId + '/' + index)
                .then(() => {
                    this.groupFieldNameSelections.splice(index, 1);
                    this.groupFieldTypeSelections.splice(index, 1);
                    this.groupFields.splice(index, 1);
                    this.saveGroup()
                        .then(() => {
                            this.clearGroupData()
                                .then(() => {
                                    this.setGroupData();
                                    this.save();
                                });
                        });
                });
        },

        initAdjustGroupIteration() {
            if (this.fieldsInGroup > 0) {
                this.showAdjustGroupIterationModal = true;
            }
        },

        cancelAdjustGroupIteration() {
            this.showAdjustGroupIterationModal = false;
            this.resetGroupIteration();
        },

        resetGroupIteration() {
            this.fieldsInGroup = this.initialFieldsInGroup;
        },

        performAdjustGroupIteration() {
            this.showAdjustGroupIterationModal = false;
            if (this.fieldsInGroup > 0 && this.fieldsInGroup < this.initialFieldsInGroup) {
                axios.post('/api/field/group/remove-repetition/' + this.fieldId + '/' + this.fieldsInGroup)
                    .then(() => {
                        this.initialFieldsInGroup = this.fieldsInGroup;
                        this.save();
                    });
            } else {
                this.initialFieldsInGroup = this.fieldsInGroup;
                this.save();
            }
        },

        saveGroup() {
            return new Promise(resolve => {
                let newFields = {'fields': []};

                this.groupFieldNameSelections.forEach((name, index) => {
                    newFields.fields.push({
                        'name': name,
                        'type': this.groupFieldTypeSelections[index],
                    });
                });

                this.setOptionsGroup(newFields);
                resolve();
            });
        },

        save() {
            this.saveData();
        },

        destroy() {
            this.toggleDeleteModal(false);
            this.destroyData();
        },

        cardsForSelect() {
            let response = [];

            this.cards.forEach(function(card) {
                if (card.section) {
                    response.push({
                        id: card.id,
                        name: card.name,
                        group:
                            card.section.step.name +
                            ' \\ ' +
                            card.section.name +
                            ' \\ ' +
                            card.name,
                    });
                }
                if (card.page) {
                    response.push({
                        id: card.id,
                        name: card.name,
                        group:
                            card.page.name + ' Page'
                    });
                }
            });

            return response;
        },

        setLocation() {
            if (this.sectionData.name) {
                return this.stepData.name + '\\' + this.sectionData.name + '\\' + this.cardData.name;
            }

            if (this.pageData.name) {
                return this.pageData.name + '\\' + this.cardData.name;
            }
        },
    }
};
</script>

<style lang="scss">
.FieldSettings {
    margin-bottom: 24px;
}
</style>
